import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class BuyerService {
  private baseUrl = environment.URL;
  private apiUrl  = environment.API_URL;

  constructor(private http: HttpClient) {}

  // ── Bids ──────────────────────────────────────────────────────────────────────

  /** POST api/bid/get-pre-bid — paginated pre-bids for buyer */
  getPrebidPrivateWeb(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}bid/get-pre-bid`, data);
  }

  /** POST api/bid/get-bid — all bids */
  getPrebid(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}bid/get-bid`, data);
  }

  /** POST api/bid/user-bid-history — buyer bid history */
  getUserHistorybid(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}bid/user-bid-history`, data);
  }

  /** POST api/bid/user-get-bid — under-approval bids for buyer */
  getUserPrebid(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}bid/user-get-bid`, data);
  }

  /** POST api/bid/bid-history — full bid history */
  getBidHistory(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}bid/bid-history`, data);
  }

  /** POST bid/update-bid — update a pre-bid */
  updatePreBid(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}bid/update-bid`, data);
  }

  // ── Orders ────────────────────────────────────────────────────────────────────

  /** POST api/order/list — buyer order list */
  getOrderList(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}order/list`, data);
  }

  /** POST api/order/create-deposit */
  createDeposit(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}order/create-deposit`, data);
  }

  /** POST api/order/create-withdraw-plan */
  createWithdrawPlan(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}order/create-withdraw-plan`, data);
  }

  /** POST api/order/create-withdraw-deposit */
  createWithdrawDeposit(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}order/create-withdraw-deposit`, data);
  }

  // ── Receipts / Transactions ───────────────────────────────────────────────────

  /** POST api/receipt/receipt-list — sold vehicle list (buyer transactions) */
  getSoldVehicleList(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}receipt/receipt-list`, data);
  }

  /** POST api/invoice-pdf/download-sale-invoice (Puppeteer renderer) — returns the invoice PDF as a blob */
  downloadSaleInvoice(data: { receiptId: string }): Observable<Blob> {
    return this.http.post(`${this.apiUrl}invoice-pdf/download-sale-invoice`, data, { responseType: 'blob' });
  }

  /** GET api/receipt/receipt-list-dashboard — sold vehicle list v2 (dashboard) */
  getSoldVehicleListDashboard(data: any): Observable<any> {
    return this.http.get(`${this.apiUrl}receipt/receipt-list-dashboard`, data);
  }

  /** POST receipt/addLedger — add a payment */
  addPayment(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}receipt/addLedger`, data);
  }

  // ── Gate Pass ─────────────────────────────────────────────────────────────────

  /** POST inventory/getPassList — gate pass list */
  getPassList(data: any): Observable<any> {
    const headers = new HttpHeaders({ 'X-Special-Param': 'delete' });
    return this.http.post(`${this.apiUrl}inventory/getPassList`, data, { headers });
  }

  // ── Plans ─────────────────────────────────────────────────────────────────────

  /** POST master/buyer-limit-list — available buyer plans */
  getPlanList(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}master/buyer-limit-list`, data);
  }

  // ── Analytics ─────────────────────────────────────────────────────────────────

  /** POST api/order/buyer-analytics */
  getBuyerAnalytics(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}order/buyer-analytics`, data);
  }

  /** GET api/order/buyer-transaction-analytics */
  getBuyerTransactionAnalytics(data: any): Observable<any> {
    return this.http.get(`${this.apiUrl}order/buyer-transaction-analytics`, { params: data });
  }

  /** POST api/analytics/consumer-statement-client-view */
  getBuyerStatement(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}analytics/consumer-statement-client-view`, data);
  }

  // ── Watchlist ─────────────────────────────────────────────────────────────────

  /** POST api/inventory/watchlistlist */
  getWatchList(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}inventory/watchlistlist`, data);
  }

  // ── Inventory ─────────────────────────────────────────────────────────────────

  /** POST api/inventory/list — private inventory list */
  getInventoryListPrivate(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}inventory/list`, data);
  }

  // ── Auction ───────────────────────────────────────────────────────────────────

  /** POST api/auction/get-auction — auction details */
  getAuctionDetail(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}auction/get-auction`, data);
  }

  // ── OTP / Auth ────────────────────────────────────────────────────────────────

  /** POST api/user/send-otp */
  userSendOtp(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}user/send-otp`, data);
  }

  // ── Tickets / User Queries ────────────────────────────────────────────────────

  /** POST contact-us/tickets — buyer support tickets */
  getUserTickets(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}contact-us/tickets`, data);
  }

  /** POST order/create-buyer-order — create buyer order */
  createBuyerPlanOrder(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}order/create-buyer-order`, data);
  }

  /** PATCH contact-us/ticket/:id — update ticket status */
  assignTicketToUser(id: any, data: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}contact-us/ticket/${id}`, data);
  }

  /** GET contact-us/getTicektComments?{queryString} */
  getTicketComments(queryString: string): Observable<any> {
    return this.http.get(`${this.baseUrl}contact-us/getTicektComments?${queryString}`);
  }

  getStatement(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}api/account/consumer-statement-client-view`, data);
  }

  auctionVehicleList(data: any) {
    return this.http.post(this.apiUrl + 'auction/auctionvehicleslist', data);
  }
}
